/**
 * useForgotPassword Composable
 * 
 * A composable that handles the forgot password functionality.
 * It manages the form state, handles form submission, and provides error handling.
 * 
 * Features:
 * - Email form state management
 * - Form submission with validation
 * - Error handling and loading states
 * - Success message display
 * 
 * @returns {Object} An object containing form state, loading state, error state, and forgot password method
 */

import { ref } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'

export function useForgotPassword() {
  const email = ref('')
  const loading = ref(false)
  const error = ref('')
  const success = ref('')
  const route = useRoute()

  const handleForgotPassword = async () => {
    try {
      loading.value = true
      error.value = ''
      success.value = ''

      // Validate input
      if (!email.value) {
        throw new Error('Please enter your email address')
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.value)) {
        throw new Error('Please enter a valid email address')
      }

      // Make API call to backend
      const response = await api.post('/forgot-password', {
        email: email.value,
        brand_slug: route.params.brandSlug
      })

      // Success response
      success.value = response.data.message || 'Password reset link has been sent to your email address.'

    } catch (err: any) {
      console.error('Forgot password error:', err)
      
      // Handle different types of errors
      if (err.response?.data?.errors) {
        // Handle validation errors from backend
        const errors = err.response.data.errors
        if (typeof errors === 'string') {
          error.value = errors
        } else if (typeof errors === 'object') {
          // If it's an object, get the first error message
          const firstError = Object.values(errors)[0]
          error.value = Array.isArray(firstError) ? firstError[0] : String(firstError)
        } else {
          error.value = 'Validation error occurred'
        }
      } else if (err.response?.data?.message) {
        error.value = err.response.data.message
      } else if (err.message) {
        error.value = err.message
      } else {
        error.value = 'Failed to send reset link. Please try again.'
      }
    } finally {
      loading.value = false
    }
  }

  return {
    email,
    loading,
    error,
    success,
    handleForgotPassword
  }
}

